import { useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Calendar, Plus, User, Users, Sparkles, Home } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const MobileLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  // Check if user is in demo mode
  const isDemoMode = user?.email === 'livedemo@myjobtools.com';

  const handleBackToHome = () => {
    logout();
    window.location.href = '/';
  };
  
  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Jobs' },
    { path: '/calendar', icon: Calendar, label: 'Calendar' },
    { path: '/new-job', icon: Plus, label: 'New', isAction: true },
    { path: '/clients', icon: Users, label: 'Clients' },
    { path: '/profile', icon: User, label: 'Profile' },
  ];

  const handleSignUp = () => {
    logout();
    // Use hard navigation to bypass React Router's PublicRoute redirect
    window.location.href = '/register';
  };

  return (
    <div className="min-h-screen bg-slate-100 noise-bg flex flex-col">
      {/* Demo Mode Banner */}
      {isDemoMode && (
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-3 relative z-50">
          <div className="max-w-[600px] mx-auto flex items-center justify-between gap-2">
            <button 
              onClick={handleBackToHome}
              className="flex items-center gap-1 text-white/90 hover:text-white transition-colors"
              data-testid="demo-back-home"
            >
              <Home className="w-4 h-4" />
              <span className="text-sm font-medium">Exit</span>
            </button>
            <div className="flex items-center gap-1">
              <Sparkles className="w-4 h-4" />
              <span className="font-medium text-sm">Demo Mode</span>
            </div>
            <button 
              onClick={handleSignUp}
              className="bg-white text-orange-600 px-3 py-1.5 rounded-full text-sm font-bold hover:bg-orange-50 transition-colors whitespace-nowrap"
              data-testid="demo-signup-cta"
            >
              Sign Up
            </button>
          </div>
        </div>
      )}
      
      {/* Main content area */}
      <main className={`flex-1 pb-24 overflow-auto relative z-10`}>
        <div className="max-w-[600px] mx-auto">
          {children}
        </div>
      </main>
      
      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-slate-dark safe-bottom z-50">
        <div className="max-w-[600px] mx-auto flex items-center justify-around h-16">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            if (item.isAction) {
              return (
                <button
                  key={item.path}
                  data-testid="nav-new-job"
                  onClick={() => navigate(item.path)}
                  className="relative -mt-6 w-14 h-14 bg-[#FF5F00] rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform"
                >
                  <Icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                </button>
              );
            }
            
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center justify-center py-2 px-4 transition-colors ${
                  isActive ? 'text-[#FF5F00]' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Icon className="w-6 h-6" strokeWidth={isActive ? 2.5 : 2} />
                <span className="text-xs mt-1 font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default MobileLayout;
